var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ShopConfig, } from '../../shop/config';
import { adsBlocked, forOwn, guessTimezone, parseQuery, } from '../../utils';
import { UAParser } from 'ua-parser-js';
import { errorToSlugError, slugError, } from '../../internals/errors/errors';
export default class Orderbuilder {
    /**
     *  Constructing a new orderbuilder,
     *  The order builder can be used to consolidate order info and send the order request.
     *
     *  This can be used for placing orders, but also requesting refunds.
     *
     *  ```ts
     *  const orderbuilder: Orderbuilder = new Orderbuilder(settings);
     *  ```
     *
     *  #### Possible errors
     *  One type of error could be thrown while initializing an orderbuilder.
     *
     *  | Error                                    | Reason                                                                    |
     *  | :--------------------------------------- | :------------------------------------------------------------------------ |
     *  | [ShopError](shoperror.html)              | No or invalid settings [ShopData](../interfaces/shopconfig.html) provided |
     */
    constructor(config) {
        if (!config) {
            throw slugError('No config provided for the orderbuilder', ['orderbuilder', 'constructor', 'error', 'config']);
        }
        this._config = new ShopConfig(config);
    }
    /**
     *  Handle the order data given in the orderbuilder options
     *  This will compile the order and send it to the API.
     *
     *  ```ts
     *  const response: PlaceOrderResponse = orderbuilder.handle(orderbuilderOptions);
     *  ```
     *
     *  #### Possible errors
     *  One type of error could be thrown while handling an order builder.
     *
     *  | Error                                    | Reason                                                                     |
     *  | :--------------------------------------- | :------------------------------------------------------------------------- |
     *  | [ShopError](shoperror.html)              | No or invalid options [ShopData](../interfaces/orderbuilder.html) provided |
     */
    handle(options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!options) {
                throw slugError('Failed to handle orderbuilder -> No options provided', ['orderbuilder', 'handle', 'error', 'options']);
            }
            if (!options.booker ||
                !options.booker.bookerInfo ||
                !options.booker.bookerInfo.email ||
                !options.booker.bookerInfo.firstname ||
                !options.booker.bookerInfo.lastname ||
                !options.booker.locale) {
                throw slugError('Failed to handle orderbuilder -> No valid booker info provided', ['orderbuilder', 'handle', 'error', 'bookerinfo']);
            }
            if (!(options.tickets && options.tickets.length) &&
                !(options.products && options.products.length) &&
                !(options.return && options.return.items && options.return.items.length)) {
                throw slugError('Failed to handle orderbuilder -> No valid items provided, need either tickets, products or return items', ['orderbuilder', 'handle', 'error', 'items']);
            }
            if (options.return && (!(options.return.items && options.return.items.length) ||
                !options.return.order_id ||
                options.return.includes_fees === undefined ||
                options.return.to_voucher === undefined)) {
                throw slugError('Failed to handle orderbuilder -> No valid return options provided', ['orderbuilder', 'handle', 'error', 'return']);
            }
            if (!options.paymentMethod) {
                throw slugError('Failed to handle orderbuilder -> No valid payment method provided', ['orderbuilder', 'handle', 'error', 'paymentmethods']);
            }
            if (options.coupons && options.coupons.length > 1) {
                throw slugError('Failed to handle orderbuilder -> Multiple coupon not implemented yet', ['orderbuilder', 'handle', 'error', 'coupons']);
            }
            const orderData = {
                receiver: {
                    email: options.booker.bookerInfo.email.value,
                    firstname: options.booker.bookerInfo.firstname.value,
                    lastname: options.booker.bookerInfo.lastname.value,
                    locale: options.booker.locale,
                },
                tickets: Orderbuilder._processTickets(options.tickets, !!options.seats),
                products: Orderbuilder._processProducts(options.products),
                return: Orderbuilder._processReturns(options.return),
                metaData: Orderbuilder._processMetadata(options.metaData),
                seatsReservationToken: options.seats ? options.seats.holdToken : undefined,
                paymentProvider: options.paymentMethod.item.guid,
                paymentProviderIssuer: options.paymentMethod.issuer ? options.paymentMethod.issuer.id : undefined,
                currency: options.paymentMethod.currency,
                stripeToken: Orderbuilder._getStripeOrderData(options.paymentMethod.stripe_token),
                coupon: options.coupons && options.coupons.length ? options.coupons[0].item.code : undefined,
                extraMetaData: Object.assign(Object.assign({}, yield Orderbuilder._getTechData(options.cartInitAt, options.seats)), options.extraTechData),
            };
            const response = yield this._send(orderData);
            return response.data;
        });
    }
    /**
     *  Send the order data to the API
     *
     *  ```ts
     *  const response: Response<PlaceOrderResponse> = this._send(orderData);
     *  ```
     *
     *  #### Possible errors
     *  Two types of errors could be thrown while sending an order to the API.
     *
     *  | Error                                    | Reason                                           |
     *  | :--------------------------------------- | :----------------------------------------------- |
     *  | [OrderApiError](orderapierror.html)      | Any of the values provided are not valid for the |
     *  |                                          | current state of the API                         |
     *  | [ApiError](apierror.html)                | Something unexpected went wrong                  |
     */
    _send(orderData) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = this._config.getNoCacheUrl(`${this._config.guid}/order`);
            try {
                return yield this._config.httpClient.post(url, orderData);
            }
            catch (error) {
                if (error &&
                    error.response &&
                    error.response.status &&
                    error.response.status >= 400 &&
                    error.response.status < 500) {
                    throw errorToSlugError(error, ['orderbuilder', 'send', 'error', 'failed'], { orderData });
                }
                else {
                    throw errorToSlugError(error, ['orderbuilder', 'send', 'error', 'fatal'], { orderData });
                }
            }
        });
    }
    /**
     *  Transform cart tickets into ticket order data
     *
     *  Explicitly static as it should never use any state on the order builder
     */
    static _processTickets(cartTickets, addSeatInfo = false) {
        if (!cartTickets || !cartTickets.length) {
            return [];
        }
        return cartTickets.map((cartTicket) => {
            const cartProducts = [];
            forOwn(cartTicket.products.map, (products) => {
                forOwn(products, (cartProduct) => {
                    cartProducts.push(cartProduct);
                });
            });
            let seatInfo = undefined;
            if (addSeatInfo && cartTicket.seatsInfo) {
                seatInfo = {
                    uuid: cartTicket.seatsInfo.objectLabel,
                    eventKeys: cartTicket.seatsInfo.eventKeys,
                };
            }
            return {
                guid: cartTicket.item.guid,
                reservation: cartTicket.reservation.reservation,
                products: Orderbuilder._processProducts(cartProducts),
                metaData: Orderbuilder._processMetadata(cartTicket.metadataList),
                seatInfo,
            };
        });
    }
    /**
     *  Transform cart products into product order data
     *
     *  Explicitly static as it should never use any state on the order builder
     */
    static _processProducts(cartProducts) {
        if (!cartProducts || !cartProducts.length) {
            return undefined;
        }
        return cartProducts.map((cartProduct) => {
            return {
                guid: cartProduct.item.guid,
                reservation: cartProduct.reservation.reservation,
                metaData: Orderbuilder._processMetadata(cartProduct.metadataList),
            };
        });
    }
    /**
     *  Transform return items into return order data
     *
     *  Explicitly static as it should never use any state on the order builder
     */
    static _processReturns(options) {
        if (!options || !options.items || !options.items.length) {
            return undefined;
        }
        const returnsOrderData = {
            order_id: options.order_id,
            includes_fees: options.includes_fees,
            to_voucher: options.to_voucher,
            reason: options.reason,
            items: options.items,
        };
        if (options.donate_percentage && options.donate_percentage > 0) {
            returnsOrderData.arbitrary = {
                percentage: options.donate_percentage,
                // A gift is by default 0 vat.
                // However, this might not be true for all countries!!!
                // We do not have this notion right now though...
                vat: 0,
            };
        }
        return returnsOrderData;
    }
    /**
     *  Transform cart metadata into metadata order data
     *
     *  Explicitly static as it should never use any state on the order builder
     */
    static _processMetadata(cartMetadataCollection) {
        if (!cartMetadataCollection || !cartMetadataCollection.length) {
            return undefined;
        }
        return cartMetadataCollection
            .filter((cartMetadata) => {
            return cartMetadata.item.extra.includes('required') || cartMetadata.value !== null;
        })
            .map((cartMetadata) => {
            return {
                guid: cartMetadata.item.guid,
                value: cartMetadata.value,
            };
        });
    }
    /**
     *  Transform cart payment method's stripe token into stripe order data if present
     *
     *  Explicitly static as it should never use any state on the order builder
     */
    static _getStripeOrderData(stripeToken) {
        return stripeToken ? { id: stripeToken.id } : undefined;
    }
    /**
     *  Transform arbitrary data into a techdata object.
     *
     *  This data is mainly for understanding order flow and debugging purposes.
     *
     *  @param initAt - The exact moment the cart was constructed
     *  @param seatsio - The seatsio info to be saved with the order for debugging purposes.
     */
    static _getTechData(initAt, seatsio) {
        return __awaiter(this, void 0, void 0, function* () {
            const dppx = +(window ? window.devicePixelRatio : 0 || 0);
            const dpi = dppx * 96;
            const uaParser = new UAParser();
            const adBlocker = yield adsBlocked();
            return {
                timeToBank: Math.ceil(((new Date()).getTime() - initAt.getTime()) / 1000),
                screen: {
                    width: window.screen.width,
                    height: window.screen.height,
                    orientation: {
                        angle: window.screen && window.screen.orientation && window.screen.orientation.angle || null,
                        type: window.screen && window.screen.orientation && window.screen.orientation.type || null,
                    },
                    dpi: dpi,
                    dpcm: dpi / 2.54,
                    dppx: dppx,
                },
                userAgent: uaParser.getResult(),
                queryParams: parseQuery(),
                timezone: guessTimezone() || null,
                adblock: adBlocker,
                referrer: document.referrer,
                doNotTrack: !!navigator.doNotTrack,
                cookieEnabled: navigator.cookieEnabled,
                seatsioHoldToken: (seatsio ? seatsio.holdToken : null) || null,
            };
        });
    }
}
//# sourceMappingURL=orderbuilder.js.map